import { MAX_SERVER_ALLOCATED_CCU, PRODUCT_TYPES, type ProductType } from "@woven/contracts";
import { useId } from "react";
import type { useCapacityDraft } from "./useCapacityDraft";
import { CapacityTooltip } from "../capacity/CapacityTooltip";

export function CapacityFields({
  draft,
  budget,
  disabled,
  unavailable,
  pendingRequest = false,
  showCalculation = true,
}: {
  draft: ReturnType<typeof useCapacityDraft>;
  budget: number | null;
  disabled: boolean;
  unavailable: string;
  pendingRequest?: boolean;
  showCalculation?: boolean;
}) {
  const id = useId();
  const { policy } = draft;
  const maximum = pendingRequest ? MAX_SERVER_ALLOCATED_CCU : draft.maximum;
  const capacity = draft.valid ? draft.capacity : null;
  return (
    <>
      <div className="capacity-field-grid">
        <label className="field">
          <span className="field-label">Product Type</span>
          <select
            className="input"
            value={draft.productType}
            disabled={disabled}
            onChange={(event) => draft.selectProduct(event.target.value as ProductType)}
          >
            {PRODUCT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label" id={`${id}-rate-label`}>
            Tick Rate (Hz)
          </span>
          <input
            aria-labelledby={`${id}-rate-label`}
            className="input"
            type="number"
            required
            min={policy.minTickRateHz}
            max={policy.maxTickRateHz}
            step={1}
            value={draft.rate}
            disabled={disabled}
            aria-invalid={!draft.validRate}
            aria-describedby={`${id}-rate`}
            onChange={(event) => draft.setRate(event.target.value)}
          />
          <span className="form-note" id={`${id}-rate`}>
            Limits aggregate publish traffic per connected client per second across channels and
            spaces, not simulation scheduling. Whole numbers from {policy.minTickRateHz}–
            {policy.maxTickRateHz} Hz.
          </span>
        </label>
        <div className="field">
          <div className="field-label">
            <label id={`${id}-ccu-label`} htmlFor={`${id}-ccu-input`}>
              Allocated CCU
            </label>{" "}
            <CapacityTooltip
              label="About allocated CCU"
              tickRateHz={draft.validRate ? draft.tickRateHz : 10}
              ccu={draft.validCcu ? draft.allocatedCcu : 25}
            />
          </div>
          <input
            id={`${id}-ccu-input`}
            aria-labelledby={`${id}-ccu-label`}
            className="input"
            type="number"
            required
            min={1}
            max={Math.max(1, maximum)}
            step={1}
            value={draft.ccu}
            disabled={disabled || budget === null || maximum === 0}
            aria-invalid={budget !== null && !draft.validCcu}
            aria-describedby={`${id}-ccu`}
            onChange={(event) => draft.setCcu(event.target.value)}
          />
          <span className="form-note" id={`${id}-ccu`}>
            {pendingRequest
              ? "While a change is pending, only an exact retry is allowed (up to 4,096 concurrent users per server)."
              : `Concurrent users · ${maximum} max at this tick rate (up to 4,096 per server).`}
          </span>
        </div>
      </div>
      <label className="field capacity-slider">
        <span className="field-label">Adjust CCU</span>
        <input
          type="range"
          min={1}
          max={Math.max(1, maximum)}
          step={1}
          value={draft.validCcu ? draft.allocatedCcu : 1}
          disabled={disabled || budget === null || maximum === 0}
          aria-describedby={`${id}-ccu`}
          onChange={(event) => draft.setCcu(event.target.value)}
        />
      </label>
      {showCalculation && (
        <section className="capacity-calculation" aria-label="Capacity calculation">
          <span className="field-label">
            Capacity{" "}
            <CapacityTooltip
              tickRateHz={draft.validRate ? draft.tickRateHz : 10}
              ccu={draft.validCcu ? draft.allocatedCcu : 25}
            />
          </span>
          <div aria-live="polite" aria-atomic="true">
            {budget === null ? (
              <p className="form-note">{unavailable}</p>
            ) : (
              <>
                <p className="metric-value">
                  {capacity === null ? "—" : capacity.toLocaleString("en-US")} units reserved
                </p>
                <p className="form-note">
                  {capacity === null ? budget : budget - capacity} units remaining after this
                  allocation · {budget} units available to configure.
                </p>
                {!draft.validRate ? (
                  <p className="form-error">Enter a whole tick rate within the product bounds.</p>
                ) : maximum === 0 ? (
                  <p className="form-error">
                    No affordable CCU at this tick rate. Reduce the tick rate or free capacity.
                  </p>
                ) : !draft.validCcu ? (
                  <p className="form-error">
                    Choose a whole CCU allocation between 1 and {maximum}.
                  </p>
                ) : (
                  <p className="form-note">
                    {draft.tickRateHz} Hz × {draft.allocatedCcu} CCU = {capacity} capacity units
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
}
